// client/src/pages/Analytics.js

import React, { useState, useEffect, useMemo } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Chart from 'react-apexcharts';
import useAnalytics from '../hooks/useAnalytics';
import useAccounts from '../hooks/useAccounts';
import { CHART_PALETTE } from '../utils/chartTheme';

const eur = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

// Le serveur tronque les dates en UTC (conteneur sans TZ = UTC). On construit donc les
// bornes directement en UTC ici : sinon `toISOString()` sur un « 1er du mois » en heure
// locale (ex. Europe/Paris, UTC+1/+2) décale la frontière d'un jour côté serveur (le
// 30 juin 22:00 UTC se ferait tronquer sur « 30 juin » au lieu du 1er juillet voulu).
const startOfMonthUTC = (y, m) => new Date(Date.UTC(y, m, 1));
const endOfMonthUTC = (y, m) => new Date(Date.UTC(y, m + 1, 0, 23, 59, 59, 999));

// Bornes calendaires strictes (1er jour 00:00 → dernier jour 23:59:59.999) pour que
// chaque préréglage couvre un mois/période complet, pas « jusqu'à aujourd'hui ».
const computeRange = (preset, customFrom, customTo) => {
  const now = new Date();
  switch (preset) {
    case 'this-month':
      return {
        from: startOfMonthUTC(now.getFullYear(), now.getMonth()),
        to: endOfMonthUTC(now.getFullYear(), now.getMonth())
      };
    case 'last-month':
      return {
        from: startOfMonthUTC(now.getFullYear(), now.getMonth() - 1),
        to: endOfMonthUTC(now.getFullYear(), now.getMonth() - 1)
      };
    case 'last-3-months':
      return {
        from: startOfMonthUTC(now.getFullYear(), now.getMonth() - 2),
        to: endOfMonthUTC(now.getFullYear(), now.getMonth())
      };
    case 'this-year':
      return {
        from: new Date(Date.UTC(now.getFullYear(), 0, 1)),
        to: new Date(Date.UTC(now.getFullYear(), 11, 31, 23, 59, 59, 999))
      };
    case 'custom':
      return {
        from: customFrom ? new Date(`${customFrom}T00:00:00.000Z`) : undefined,
        to: customTo ? new Date(`${customTo}T23:59:59.999Z`) : undefined
      };
    case 'all':
    default:
      return { from: undefined, to: undefined };
  }
};

// Composants de date LOCAUX (pas toISOString, qui shifterait le jour affiché dans le
// picker près de minuit) : ce qui compte ici, c'est le jour que l'utilisateur voit/tape.
const pad2 = (n) => String(n).padStart(2, '0');
const toInputDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const MetricCard = ({ label, value, color, hint }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body>
      <div className="text-muted" style={{ fontSize: 13 }}>
        {label}
      </div>
      <div className="fw-bold" style={{ fontSize: 26, color }}>
        {value}
      </div>
      {hint && (
        <div className="text-muted" style={{ fontSize: 11 }}>
          {hint}
        </div>
      )}
    </Card.Body>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body>
      <Card.Title as="h5">{title}</Card.Title>
      {children}
    </Card.Body>
  </Card>
);

// Tableau déroulant du détail par catégorie (montant, part, nb de transactions,
// moyenne) avec ventilation en sous-catégories au clic.
const CategoryBreakdown = ({ data }) => {
  const [expanded, setExpanded] = useState({});
  if (!data || !data.categories || data.categories.length === 0) {
    return <p className="text-muted mt-3">Aucune dépense catégorisée sur la période.</p>;
  }
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const max = data.categories[0]?.total || 1;

  return (
    <Table hover responsive size="sm" className="mb-0 align-middle">
      <thead>
        <tr>
          <th style={{ width: 26 }} />
          <th>Catégorie</th>
          <th className="text-end">Montant</th>
          <th className="text-end d-none d-md-table-cell">Part</th>
          <th className="text-center d-none d-md-table-cell">Transac.</th>
          <th className="text-end d-none d-md-table-cell">Moyenne</th>
        </tr>
      </thead>
      <tbody>
        {data.categories.map((c) => {
          const key = c.categoryId || 'uncat';
          const hasSubs = c.subcategories && c.subcategories.length > 0;
          const open = !!expanded[key];
          return (
            <React.Fragment key={key}>
              <tr
                style={{ cursor: hasSubs ? 'pointer' : 'default' }}
                onClick={() => hasSubs && toggle(key)}
              >
                <td className="text-muted text-center">{hasSubs ? (open ? '▾' : '▸') : ''}</td>
                <td>
                  <span
                    className="d-inline-block rounded-circle me-2"
                    style={{ width: 10, height: 10, background: c.color }}
                  />
                  {c.name}
                </td>
                <td className="text-end fw-semibold">
                  {eur(c.total)}
                  <div className="d-md-none text-muted" style={{ fontSize: 11 }}>
                    {c.percent}% · {c.count} tx
                  </div>
                </td>
                <td className="text-end d-none d-md-table-cell" style={{ minWidth: 130 }}>
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    <div
                      className="rounded"
                      style={{
                        height: 6,
                        width: `${Math.max(4, (c.total / max) * 60)}px`,
                        background: c.color,
                        opacity: 0.75
                      }}
                    />
                    <span className="text-muted" style={{ fontSize: 13 }}>
                      {c.percent}%
                    </span>
                  </div>
                </td>
                <td className="text-center d-none d-md-table-cell text-muted">{c.count}</td>
                <td className="text-end d-none d-md-table-cell text-muted">{eur(c.avg)}</td>
              </tr>
              {open &&
                c.subcategories.map((s) => (
                  <tr key={`${key}-${s.categoryId || s.name}`} className="table-light">
                    <td />
                    <td className="ps-4">
                      <span
                        className="d-inline-block rounded-circle me-2"
                        style={{ width: 8, height: 8, background: s.color }}
                      />
                      <span className="text-muted">{s.name}</span>
                    </td>
                    <td className="text-end">{eur(s.total)}</td>
                    <td className="text-end d-none d-md-table-cell text-muted">{s.percent}%</td>
                    <td className="text-center d-none d-md-table-cell text-muted">{s.count}</td>
                    <td className="d-none d-md-table-cell" />
                  </tr>
                ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
};

function Analytics() {
  const { summary, categoryBreakdown, monthly, budgets, subscriptions, loading, error, load } =
    useAnalytics();
  const { accounts, fetchAccounts, fetchBalanceHistory } = useAccounts();
  const [preset, setPreset] = useState('this-month');
  const [accountId, setAccountId] = useState('');
  const [customFrom, setCustomFrom] = useState(() => {
    const now = new Date();
    return toInputDate(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [customTo, setCustomTo] = useState(() => toInputDate(new Date()));
  const [balanceData, setBalanceData] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  useEffect(() => {
    fetchAccounts().catch(() => {});
  }, [fetchAccounts]);

  useEffect(() => {
    if (preset === 'custom' && (!customFrom || !customTo)) return;
    const { from, to } = computeRange(preset, customFrom, customTo);
    load({
      from: from ? from.toISOString() : undefined,
      to: to ? to.toISOString() : undefined,
      accountId: accountId || undefined
    }).catch(() => {});
  }, [preset, customFrom, customTo, accountId, load]);

  // Historique du solde : nécessite un compte précis (le solde n'a pas de sens « tous comptes »),
  // borné à la période sélectionnée en haut de page.
  useEffect(() => {
    if (!accountId) {
      setBalanceData(null);
      return undefined;
    }
    if (preset === 'custom' && (!customFrom || !customTo)) return undefined;
    let cancelled = false;
    setBalanceLoading(true);
    const { from, to } = computeRange(preset, customFrom, customTo);
    fetchBalanceHistory(accountId, {
      from: from ? from.toISOString() : undefined,
      to: to ? to.toISOString() : undefined
    })
      .then((data) => {
        if (!cancelled) setBalanceData(data);
      })
      .catch(() => {
        if (!cancelled) setBalanceData([]);
      })
      .finally(() => {
        if (!cancelled) setBalanceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, preset, customFrom, customTo, fetchBalanceHistory]);

  const bars = useMemo(() => {
    const points = monthly.points || [];
    // Libellés compacts selon la granularité (jour/semaine : « 12 juil. », mois : « 2026-07 »)
    const label = (p) =>
      monthly.granularity === 'month'
        ? p.period
        : new Date(p.period).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    return {
      series: [
        { name: 'Revenus', data: points.map((m) => Math.round(m.income)) },
        { name: 'Dépenses', data: points.map((m) => Math.round(m.expense)) }
      ],
      options: {
        chart: { type: 'bar', toolbar: { show: false }, stacked: false },
        colors: [CHART_PALETTE[0], CHART_PALETTE[1]],
        plotOptions: { bar: { columnWidth: '65%', borderRadius: 3 } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: points.map(label),
          tickPlacement: 'on',
          labels: { rotate: -45, hideOverlappingLabels: true }
        },
        yaxis: { labels: { formatter: (v) => `${Math.round(v)} €` } },
        legend: { position: 'top' },
        tooltip: { y: { formatter: (v) => eur(v) } }
      }
    };
  }, [monthly]);

  const granularityLabel =
    { day: 'par jour', week: 'par semaine', month: 'par mois' }[monthly.granularity] || '';

  const balance = useMemo(() => {
    const data = balanceData || [];
    return {
      series: [
        { name: 'Solde', data: data.map((i) => Number(Number(i.runningBalance).toFixed(2))) }
      ],
      options: {
        chart: {
          type: 'area',
          toolbar: { show: true, autoSelected: 'zoom' },
          zoom: { autoScaleYaxis: true }
        },
        colors: [CHART_PALETTE[2]],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: { type: 'datetime', categories: data.map((i) => i.date) },
        yaxis: { labels: { formatter: (v) => `${Math.round(v)} €` } },
        tooltip: { x: { format: 'dd MMM yyyy' }, y: { formatter: (v) => eur(v) } },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1, stops: [0, 90, 100] }
        }
      }
    };
  }, [balanceData]);

  const donut = useMemo(() => {
    const cats = summary?.byCategory?.filter((c) => c.total > 0) || [];
    return {
      series: cats.map((c) => Math.round(c.total * 100) / 100),
      options: {
        labels: cats.map((c) => c.name),
        colors: cats.map((c) => c.color),
        legend: { position: 'bottom' },
        dataLabels: { enabled: true, formatter: (val) => `${Math.round(val)}%` },
        tooltip: { y: { formatter: (v) => eur(v) } },
        stroke: { width: 1 }
      }
    };
  }, [summary]);

  const subsMonthlyTotal = useMemo(
    () => subscriptions.filter((s) => s.active).reduce((sum, s) => sum + (s.monthlyCost || 0), 0),
    [subscriptions]
  );

  const budgetVariant = (percent) => {
    if (percent > 100) return 'danger';
    if (percent > 80) return 'warning';
    return 'success';
  };

  const emptyPeriod = summary && summary.income === 0 && summary.expense === 0;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h1 className="mb-0">Analyse des dépenses</h1>
        <div className="d-flex gap-2 flex-wrap">
          <Form.Select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            style={{ maxWidth: 220 }}
          >
            <option value="">Tous les comptes</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Form.Select>
          <Form.Select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            <option value="this-month">Ce mois-ci</option>
            <option value="last-month">Mois dernier</option>
            <option value="last-3-months">3 derniers mois</option>
            <option value="this-year">Cette année</option>
            <option value="all">Tout l&apos;historique</option>
            <option value="custom">Période personnalisée</option>
          </Form.Select>
          {preset === 'custom' && (
            <>
              <Form.Control
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{ maxWidth: 160 }}
              />
              <Form.Control
                type="date"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{ maxWidth: 160 }}
              />
            </>
          )}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <>
          {/* Ligne 1 : évolution mensuelle (tout l'historique) */}
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <ChartCard title={`Évolution (${granularityLabel})`}>
                {(monthly.points || []).length > 0 ? (
                  <Chart options={bars.options} series={bars.series} type="bar" height={320} />
                ) : (
                  <p className="text-muted mt-3">Pas encore de données à afficher.</p>
                )}
              </ChartCard>
            </Col>
          </Row>

          {/* Ligne 2 : évolution du solde (par compte) */}
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <ChartCard title="Évolution du solde">
                {!accountId ? (
                  <p className="text-muted mt-3">
                    Sélectionnez un compte (en haut à droite) pour afficher l&apos;évolution de son
                    solde.
                  </p>
                ) : balanceLoading ? (
                  <Spinner animation="border" />
                ) : balance.series[0].data.length > 0 ? (
                  <Chart
                    options={balance.options}
                    series={balance.series}
                    type="area"
                    height={320}
                  />
                ) : (
                  <p className="text-muted mt-3">
                    Aucun historique de solde pour ce compte (colonne « Solde Courant » non importée
                    ?).
                  </p>
                )}
              </ChartCard>
            </Col>
          </Row>

          {emptyPeriod && (
            <Alert variant="info" className="py-2">
              Aucune transaction sur la période sélectionnée. Les graphes ci-dessus couvrent tout
              l&apos;historique ; élargissez la période pour les statistiques ci-dessous.
            </Alert>
          )}

          {/* Cartes de synthèse (période sélectionnée) */}
          {summary && (
            <Row className="g-3 mb-4">
              <Col xs={6} lg={3}>
                <MetricCard
                  label="Revenus"
                  value={eur(summary.income)}
                  color="#1D9E75"
                  hint={
                    summary.plannedSalary > 0 && summary.plannedSalary > summary.actualIncome
                      ? 'salaire prévu'
                      : undefined
                  }
                />
              </Col>
              <Col xs={6} lg={3}>
                <MetricCard label="Dépenses" value={eur(summary.expense)} color="#E24B4A" />
              </Col>
              <Col xs={6} lg={3}>
                <MetricCard label="Épargne" value={eur(summary.savings)} />
              </Col>
              <Col xs={6} lg={3}>
                <MetricCard label="Taux d'épargne" value={`${summary.savingsRate} %`} />
              </Col>
            </Row>
          )}

          {/* Donut + budgets */}
          <Row className="g-3 mb-4">
            <Col lg={6}>
              <ChartCard title="Dépenses par catégorie">
                {donut.series.length > 0 ? (
                  <Chart options={donut.options} series={donut.series} type="donut" height={320} />
                ) : (
                  <p className="text-muted mt-3">Aucune dépense catégorisée sur la période.</p>
                )}
              </ChartCard>
            </Col>
            <Col lg={6}>
              <ChartCard title="Budgets du mois">
                {budgets.length === 0 ? (
                  <p className="text-muted mt-3">
                    Aucun budget défini. Ajoutez un budget mensuel à une catégorie depuis la page
                    Catégories.
                  </p>
                ) : (
                  budgets.map((b) => (
                    <div key={b.categoryId} className="mb-3">
                      <div className="d-flex justify-content-between mb-1" style={{ fontSize: 14 }}>
                        <span>{b.name}</span>
                        <span className={b.percent > 100 ? 'text-danger fw-bold' : 'text-muted'}>
                          {eur(b.spent)} / {eur(b.budget)}
                        </span>
                      </div>
                      <ProgressBar
                        now={Math.min(b.percent, 100)}
                        variant={budgetVariant(b.percent)}
                      />
                    </div>
                  ))
                )}
              </ChartCard>
            </Col>
          </Row>

          {/* Détail par catégorie */}
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Card.Title as="h5" className="mb-0">
                      Détail par catégorie
                    </Card.Title>
                    {categoryBreakdown && categoryBreakdown.categories.length > 0 && (
                      <Badge bg="secondary" pill>
                        {eur(categoryBreakdown.totalExpense)}
                      </Badge>
                    )}
                  </div>
                  <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                    Clique une catégorie pour dérouler ses sous-catégories.
                  </div>
                  <CategoryBreakdown data={categoryBreakdown} />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Abonnements */}
          <Row className="g-3">
            <Col xs={12}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Card.Title as="h5" className="mb-0">
                      Abonnements détectés
                    </Card.Title>
                    {subscriptions.length > 0 && (
                      <Badge bg="primary" pill>
                        ~{eur(subsMonthlyTotal)}/mois
                      </Badge>
                    )}
                  </div>
                  {subscriptions.length === 0 ? (
                    <p className="text-muted mt-3">Aucun prélèvement récurrent détecté.</p>
                  ) : (
                    <Table hover responsive size="sm" className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>Marchand</th>
                          <th>Fréquence</th>
                          <th className="text-center">Occ.</th>
                          <th className="text-end">Montant</th>
                          <th className="text-end">Par an</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptions.map((s) => (
                          <tr key={s.label} className={s.active ? '' : 'text-muted'}>
                            <td className="text-capitalize">
                              <span className="d-inline-flex align-items-center">
                                <span
                                  className={`me-2 rounded-circle d-inline-block ${s.active ? 'bg-success' : 'bg-secondary'}`}
                                  style={{ width: 8, height: 8 }}
                                  title={s.active ? 'Actif' : 'Probablement résilié'}
                                />
                                {s.label}
                              </span>
                              <div>
                                <Badge
                                  bg="light"
                                  text="dark"
                                  style={{ borderLeft: `3px solid ${s.categoryColor}` }}
                                >
                                  {s.categoryName}
                                </Badge>
                                {!s.active && (
                                  <Badge bg="warning" text="dark" className="ms-1">
                                    Résilié ?
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="text-capitalize">{s.frequency}</td>
                            <td className="text-center text-muted">{s.occurrences}</td>
                            <td className="text-end">{eur(s.amount)}</td>
                            <td className="text-end text-muted">{eur(s.yearlyCost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default Analytics;
